import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import type { CampaignRanking, CampaignRankingRawMetrics } from '../types/campaign.types';
import { calculateCampaignRanking } from '@/lib/algorithms/calculateCampaignRanking';
import { campaignKeys } from '../services/campaign.keys';

export const useCampaignPerformanceRanking = () => {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: campaignKeys.ranking(),
    queryFn: async (): Promise<CampaignRanking[]> => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }

      // Fetch campaigns and their metrics
      const { data: campaignsData, error: campaignsError } = await supabase
        .from('campaigns')
        .select(`
          id,
          name,
          total_budget,
          spent_budget,
          status,
          start_date,
          end_date,
          created_at
        `)
        .eq('organization_id', currentOrganization.id);

      if (campaignsError) throw campaignsError;
      if (!campaignsData || campaignsData.length === 0) return [];

      // Fetch actual deliverables data for accurate views and engagement
      const aggregatesPromises = campaignsData.map(async (c) => {
        const { data, error } = await supabase.rpc('get_deliverable_aggregates', {
          p_campaign_id: c.id,
        });
        if (error) console.error(`Error fetching aggregates for campaign ${c.id}:`, error);
        return { campaignId: c.id, aggregates: (data || []) as any[] };
      });
      const aggregatesResults = await Promise.all(aggregatesPromises);

      // Map raw data to standard metrics interface
      const metricsList = campaignsData.map((c) => {
        const aggResult = aggregatesResults.find((a) => a.campaignId === c.id);
        const aggregates = aggResult?.aggregates || [];

        let totalViews = 0;
        let totalEngagement = 0;

        aggregates.forEach((agg) => {
          totalViews += Number(agg.total_views || 0);
          totalEngagement += Number(agg.total_engagement || 0);
        });

        // Calculate timeline progress
        let timelineProgress = 0;
        const start = c.start_date ? new Date(c.start_date) : null;
        const end = c.end_date ? new Date(c.end_date) : null;
        const today = new Date();
        if (start && end && start.getTime() < end.getTime()) {
          const totalDays = end.getTime() - start.getTime();
          const elapsed = today.getTime() - start.getTime();
          timelineProgress = Math.min(100, Math.max(0, (elapsed / totalDays) * 100));
        }

        const rawMetrics: CampaignRankingRawMetrics = {
          totalViews,
          totalEngagement,
          spend: c.spent_budget || 0,
          contractedDeliverables: 10,
          completedDeliverables: c.status === 'completed' ? 10 : 5,
          deliverableProgress: c.status === 'completed' ? 100 : 50,
          timelineProgress,
          paymentCompletionPercentage: c.status === 'completed' ? 100 : 50,
        };

        return {
          id: c.id,
          name: c.name,
          rawMetrics,
        };
      });

      // Find min/max values for normalization
      let minViews = Infinity;
      let maxViews = -Infinity;
      let minEngagement = Infinity;
      let maxEngagement = -Infinity;
      let minER = Infinity;
      let maxER = -Infinity;

      metricsList.forEach((m) => {
        const { totalViews, totalEngagement } = m.rawMetrics;
        const er = totalViews > 0 ? (totalEngagement / totalViews) * 100 : 0;

        minViews = Math.min(minViews, totalViews);
        maxViews = Math.max(maxViews, totalViews);
        minEngagement = Math.min(minEngagement, totalEngagement);
        maxEngagement = Math.max(maxEngagement, totalEngagement);
        minER = Math.min(minER, er);
        maxER = Math.max(maxER, er);
      });

      const safeMinMax = {
        minViews: minViews === Infinity ? 0 : minViews,
        maxViews: maxViews === -Infinity ? 0 : maxViews,
        minEngagement: minEngagement === Infinity ? 0 : minEngagement,
        maxEngagement: maxEngagement === -Infinity ? 0 : maxEngagement,
        minER: minER === Infinity ? 0 : minER,
        maxER: maxER === -Infinity ? 0 : maxER,
      };

      const rankings = metricsList.map((m) =>
        calculateCampaignRanking(m.id, m.name, m.rawMetrics, safeMinMax)
      );

      return rankings.sort((a, b) => b.overallScore - a.overallScore);
    },
    enabled: !!currentOrganization?.id,
    staleTime: 0,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};
